import { Component, OnDestroy, OnChanges, SimpleChanges, Input, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { StringHelper } from 'src/app/helper/string/string-helper';
import { DateTime } from 'src/app/helper/date-time/date-time';
import * as moment from 'moment-timezone';

@Component({
	selector: 'app-duration',
	templateUrl: './duration.component.html',
	styleUrls: ['./duration.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DurationComponent implements AfterViewInit, OnChanges, OnDestroy {
	private timerPromise: Subscription = new Subscription();
	public inProgressTimer: string = "";
	private viewInitialized = false;

	@Input()
	public startedAt: string | Date = "";

	@Input()
	public endedAt: string | Date = "";

	public get isEnded(): boolean {
		return !!this.endedAt;
	}

	constructor(private changeDetector: ChangeDetectorRef) { }

	public ngAfterViewInit(): void {
		this.viewInitialized = true;
		this.timerPromise = interval(10000).subscribe(() => this.updateLabel());
		this.updateLabel();
	}

	/**
	 * startedAt/endedAt routinely arrive (or change) after the view is already
	 * initialized — e.g. site-performance/store.page.ts's sitePerformance starts
	 * as just {id, site_id, route_id} and only gets the real started_at once its
	 * show() API call resolves. Without reacting to that here, the display was
	 * stuck at whatever ngAfterViewInit computed from the empty default
	 * ("00:00:00") until the next 10s tick happened to catch it.
	 */
	public ngOnChanges(changes: SimpleChanges): void {
		if (this.viewInitialized && (changes.startedAt || changes.endedAt)) {
			this.updateLabel();
		}
	}

	private updateLabel() {
		try {
			let duration = DateTime.duration(this.startedAt, this.endedAt);
			this.inProgressTimer = DateTime.printDuration(duration);
		} catch (error) {
			// A bare `interval(...).subscribe(callback)` with no error handler
			// unsubscribes PERMANENTLY the first time the callback throws — it
			// never ticks again. Swallowing here means one bad computation
			// can't silently freeze this display forever; the next tick (or
			// the next input change) gets another chance.
			this.inProgressTimer = "00:00:00";
		}
		this.changeDetector.detectChanges();
	}

	public ngOnDestroy() {
		this.timerPromise.unsubscribe();
	}
}
